import { UserButton, useUser } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router";

import {
  accountBillingClerkPageUrl,
  mountAccountBillingClerkPages,
  queryAccountBilling,
} from "../account-billing";
import { useApi } from "../api-context";
import { clerkAuthAppearanceFor } from "../clerk-auth-appearance";
import { clerkProfileSyncPayload } from "../clerk-profile-sync";
import { isDesktopRuntime } from "../desktop";
import { installerTargets, type InstallerTarget } from "../desktop-installer";
import { AccountBillingPanel } from "./AccountBillingPanel";
import { BrandMark } from "./BrandMark";
import { DesktopInstallerDialog } from "./DesktopInstallerDialog";
import { ThemeToggle, useColorTheme } from "./ThemeToggle";
import { personDisplayName, personInitial } from "../workspace-chrome";

const RAIL_STORAGE_KEY = "nlkb.workspace-rail";

const workspaceNavigation = [
  { path: "knowledge", icon: "knowledge", label: "Knowledge" },
  { path: "search", icon: "search", label: "Search" },
  { path: "skills", icon: "skills", label: "Skills" },
  { path: "templates", icon: "templates", label: "Templates" },
  { path: "conflicts", icon: "conflicts", label: "Conflicts" },
  { path: "connections", icon: "connections", label: "Connections" },
  { path: "audit", icon: "audit", label: "Audit trail" },
  { path: "team", icon: "team", label: "Team" },
] as const;

type RailIconName =
  (typeof workspaceNavigation)[number]["icon"] | "admin" | "download";

function readRailExpanded(): boolean {
  try {
    return window.localStorage.getItem(RAIL_STORAGE_KEY) !== "collapsed";
  } catch {
    return true;
  }
}

function persistRailExpanded(expanded: boolean): void {
  try {
    window.localStorage.setItem(
      RAIL_STORAGE_KEY,
      expanded ? "expanded" : "collapsed",
    );
  } catch {
    // Ignore quota and private-mode failures; the rail still toggles in memory.
  }
}

function RailIcon({ name }: { readonly name: RailIconName | "fold" }) {
  return (
    <svg
      className="rail-icon"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="square"
      strokeLinejoin="miter"
    >
      {railIconGraphic(name)}
    </svg>
  );
}

function railIconGraphic(name: RailIconName | "fold"): ReactNode {
  switch (name) {
    case "knowledge":
      return (
        <>
          <path d="M4.5 3.5h8l3 3v10h-11z" />
          <path d="M12.5 3.5v3h3" />
          <path d="M7 10h6M7 13h4" />
        </>
      );
    case "search":
      return (
        <>
          <circle cx="8.5" cy="8.5" r="4.2" />
          <path d="M11.7 11.7 16 16" />
        </>
      );
    case "conflicts":
      return (
        <>
          <path d="M10 3.5v5.5" />
          <path d="M10 9 5.5 15.5" />
          <path d="M10 9l4.5 6.5" />
          <path d="M5.5 15.5H4M14.5 15.5H16" />
        </>
      );
    case "connections":
      return (
        <>
          <circle cx="5" cy="10" r="2.2" />
          <circle cx="15" cy="10" r="2.2" />
          <path d="M7.2 10h5.6" />
        </>
      );
    case "audit":
      return (
        <>
          <path d="M5 3.5v13" />
          <path d="M5 6.5h2.5M5 10h2.5M5 13.5h2.5" />
          <path d="M9.5 6.5H16M9.5 10h4.5M9.5 13.5H16" />
        </>
      );
    case "team":
      return (
        <>
          <circle cx="7.2" cy="6.8" r="2.1" />
          <circle cx="13.2" cy="7.2" r="1.7" />
          <path d="M3.6 15.5c.4-2.8 1.9-4.3 3.6-4.3s3.2 1.5 3.6 4.3" />
          <path d="M11.2 15.5c.3-2.2 1.4-3.3 2.6-3.3 1.3 0 2.3 1.1 2.6 3.3" />
        </>
      );
    case "templates":
      return (
        <>
          <path d="M6 4.5h8v11H6z" />
          <path d="M8 7h4M8 10h4M8 13h2.5" />
          <path d="M4.5 6.5v10h7" />
        </>
      );
    case "skills":
      return (
        <>
          <path d="M5.5 3.5h9v13l-4.5-2.6-4.5 2.6z" />
          <path d="M8 7.5h4M8 10.5h2.5" />
        </>
      );
    case "admin":
      return (
        <>
          <rect x="3.5" y="4" width="13" height="12" />
          <path d="M3.5 7h13" />
          <path d="M6.5 10h7M6.5 13h4.5" />
        </>
      );
    case "download":
      return (
        <>
          <path d="M10 3.5v8.5" />
          <path d="M6.5 8.5 10 12l3.5-3.5" />
          <path d="M4.5 16.5h11" />
        </>
      );
    case "fold":
      return <path d="M12.2 4.5 6.8 10l5.4 5.5" />;
  }
}

function railLinkClass(isActive: boolean): string {
  return `rail-link${isActive ? " rail-link--active" : ""}`;
}

function templatesPath(organizationId: string): string {
  return `/org/${organizationId}/templates`;
}

function skillsPath(organizationId: string): string {
  return `/org/${organizationId}/skills`;
}

function adminPath(organizationId: string): string {
  return organizationId.length > 0 ? `/org/${organizationId}/admin` : "/admin";
}

export function WorkspaceRail({
  organizationId,
  showWorkspaceNav,
  showTemplates,
  showAdmin,
  billingAccountId = null,
  plan = null,
  organizationRole,
}: {
  readonly organizationId: string;
  readonly showWorkspaceNav: boolean;
  readonly showTemplates: boolean;
  readonly showAdmin: boolean;
  readonly billingAccountId?: string | null;
  readonly plan?: "solo" | "teams" | "partner" | "platform" | null;
  readonly organizationRole?: "org_owner" | "org_admin" | "member" | undefined;
}) {
  const [expanded, setExpanded] = useState(() => readRailExpanded());
  const location = useLocation();
  const api = useApi();
  const conflicts = useQuery({
    queryKey: ["conflicts", organizationId, "open"],
    queryFn: () => api.conflicts(organizationId),
    enabled: showWorkspaceNav && organizationId.length > 0,
  });
  const openConflictCount = conflicts.data?.conflicts.length ?? 0;
  const onConflictsRoute = location.pathname.includes(
    `/org/${organizationId}/conflicts`,
  );
  const navigation = workspaceNavigation.filter(
    (item) =>
      item.path !== "conflicts" || openConflictCount > 0 || onConflictsRoute,
  );

  const toggleExpanded = () => {
    setExpanded((current) => {
      const next = !current;
      persistRailExpanded(next);
      return next;
    });
  };

  return (
    <aside
      className={`product-rail${expanded ? " product-rail--expanded" : ""}`}
    >
      <div className="rail-head">
        <NavLink
          className="rail-brand"
          to="/"
          aria-label="Knowledge Base by Northern Logic"
        >
          <BrandMark className="rail-brand__mark" />
          <span className="rail-brand__wordmark" aria-hidden="true">
            <span className="rail-brand__product">Knowledge Base</span>
            <span className="rail-brand__by">by Northern Logic</span>
          </span>
        </NavLink>
        <button
          className="rail-toggle"
          type="button"
          aria-expanded={expanded}
          aria-controls="workspace-nav"
          aria-label={expanded ? "Collapse navigation" : "Expand navigation"}
          title={expanded ? "Collapse navigation" : "Expand navigation"}
          onClick={toggleExpanded}
        >
          <RailIcon name="fold" />
        </button>
      </div>
      <nav id="workspace-nav" aria-label="Workspace">
        {showWorkspaceNav ? (
          navigation.map((item) => (
            <NavLink
              key={item.path}
              className={({ isActive }) => railLinkClass(isActive)}
              to={`/org/${organizationId}/${item.path}`}
              title={expanded ? undefined : item.label}
            >
              <RailIcon name={item.icon} />
              <span className="rail-label">
                {item.label}
                {item.path === "conflicts" && openConflictCount > 0 ? (
                  <span className="rail-count">{openConflictCount}</span>
                ) : null}
              </span>
            </NavLink>
          ))
        ) : showTemplates ? (
          <>
            <NavLink
              className={({ isActive }) => railLinkClass(isActive)}
              to={templatesPath(organizationId)}
              title={expanded ? undefined : "Templates"}
            >
              <RailIcon name="templates" />
              <span className="rail-label">Templates</span>
            </NavLink>
            <NavLink
              className={({ isActive }) => railLinkClass(isActive)}
              to={skillsPath(organizationId)}
              title={expanded ? undefined : "Skills"}
            >
              <RailIcon name="skills" />
              <span className="rail-label">Skills</span>
            </NavLink>
          </>
        ) : null}
        {showAdmin ? (
          <NavLink
            className={({ isActive }) => railLinkClass(isActive)}
            to={adminPath(organizationId)}
            title={expanded ? undefined : "Admin"}
          >
            <RailIcon name="admin" />
            <span className="rail-label">Admin</span>
          </NavLink>
        ) : null}
      </nav>
      <div className="rail-end">
        <ThemeToggle variant="rail" />
        {isDesktopRuntime() ? null : (
          <RailDesktopDownload expanded={expanded} />
        )}
        <RailAccount
          expanded={expanded}
          organizationId={organizationId}
          billingAccountId={billingAccountId}
          plan={plan}
          organizationRole={organizationRole}
        />
      </div>
    </aside>
  );
}

function RailDesktopDownload({ expanded }: { readonly expanded: boolean }) {
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [installerTarget, setInstallerTarget] =
    useState<InstallerTarget | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        rootRef.current?.contains(event.target) !== true
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="rail-download" ref={rootRef}>
      <button
        className="rail-link"
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="menu"
        title={expanded ? undefined : "Get the desktop app"}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <RailIcon name="download" />
        <span className="rail-label">Get the desktop app</span>
      </button>
      {open ? (
        <div className="rail-download__menu" id={menuId} role="menu">
          {installerTargets.map((target) => (
            <button
              key={`${target.operatingSystem}-${target.architecture}`}
              type="button"
              role="menuitem"
              onClick={() => {
                setInstallerTarget(target);
                setOpen(false);
              }}
            >
              {target.label}
            </button>
          ))}
        </div>
      ) : null}
      <DesktopInstallerDialog
        target={installerTarget}
        onClose={() => {
          setInstallerTarget(null);
        }}
      />
    </div>
  );
}

function BillingIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <rect x="3.5" y="5" width="13" height="10" />
      <path d="M3.5 8.5h13" />
      <path d="M6.5 11.5h4" />
    </svg>
  );
}

function RailAccount({
  expanded,
  organizationId,
  billingAccountId,
  plan,
  organizationRole,
}: {
  readonly expanded: boolean;
  readonly organizationId: string;
  readonly billingAccountId: string | null;
  readonly plan: "solo" | "teams" | "partner" | "platform" | null;
  readonly organizationRole: "org_owner" | "org_admin" | "member" | undefined;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const theme = useColorTheme();
  const { user } = useUser();
  const accountRef = useRef<HTMLDivElement>(null);
  const syncedProfileKey = useRef<string | null>(null);
  const currentPrincipal = useQuery({
    queryKey: ["current-principal"],
    queryFn: () => api.currentPrincipal(),
  });
  const updateProfile = useMutation({
    mutationFn: (input: { display_name?: string; primary_email?: string }) =>
      api.updateCurrentPrincipal(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["current-principal"] });
      await queryClient.invalidateQueries({ queryKey: ["connections"] });
      await queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });
  const identity = {
    firstName: user?.firstName ?? null,
    lastName: user?.lastName ?? null,
    fullName: user?.fullName ?? null,
    username: user?.username ?? null,
    email: user?.primaryEmailAddress?.emailAddress ?? null,
    fallback: currentPrincipal.data?.display_name ?? null,
  };
  useEffect(() => {
    if (user == null || currentPrincipal.data === undefined) return;
    const payload = clerkProfileSyncPayload({
      storedDisplayName: currentPrincipal.data.display_name,
      storedEmail: currentPrincipal.data.primary_email,
      clerk: {
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        fullName: user.fullName ?? null,
        username: user.username ?? null,
        email: user.primaryEmailAddress?.emailAddress ?? null,
      },
    });
    if (payload === null) return;
    const key = JSON.stringify(payload);
    if (syncedProfileKey.current === key) return;
    syncedProfileKey.current = key;
    updateProfile.mutate(payload);
  }, [currentPrincipal.data, updateProfile, user]);
  const billingUsage = useQuery({
    queryKey: ["billing-usage", billingAccountId],
    queryFn: () => api.billingUsage(billingAccountId ?? ""),
    enabled: queryAccountBilling({
      billingAccountId,
      plan,
      organizationRole,
    }),
    retry: false,
  });
  const showBilling = mountAccountBillingClerkPages({
    queryEligible: queryAccountBilling({
      billingAccountId,
      plan,
      organizationRole,
    }),
    plan,
    usage: billingUsage.data,
    usageFailed: billingUsage.isError,
  });
  const name = personDisplayName(identity);
  const initial = personInitial(identity);
  const clerkAppearance = clerkAuthAppearanceFor(theme);

  const openAccountMenu = () => {
    const buttons = accountRef.current?.querySelectorAll("button");
    if (buttons === undefined) return;
    for (const button of buttons) {
      if (!button.classList.contains("rail-account__name")) {
        button.click();
        return;
      }
    }
  };

  return (
    <div
      className="rail-account"
      ref={accountRef}
      title={expanded ? undefined : name}
    >
      <div className="rail-account__avatar-slot">
        <span className="rail-account__initial" aria-hidden="true">
          {initial}
        </span>
        <UserButton
          appearance={{
            variables: clerkAppearance.variables,
            elements: {
              rootBox: {
                className: "rail-account__root",
                width: "26px",
                height: "26px",
              },
              userButtonBox: {
                className: "rail-account__box",
                width: "26px",
                height: "26px",
              },
              userButtonTrigger: {
                className: "rail-account__trigger",
                width: "26px",
                height: "26px",
                padding: "0",
                borderRadius: "50%",
              },
              avatarBox: {
                className: "rail-account__avatar",
                width: "26px",
                height: "26px",
                borderRadius: "50%",
                backgroundColor: "transparent",
              },
            },
          }}
          userProfileProps={{
            appearance: {
              variables: clerkAppearance.variables,
              elements: clerkAppearance.elements,
            },
          }}
        >
          {showBilling ? (
            <UserButton.UserProfilePage
              label="Billing"
              url={accountBillingClerkPageUrl}
              labelIcon={<BillingIcon />}
            >
              {billingUsage.data === undefined ? (
                <p className="billing-meter__note">Loading billing…</p>
              ) : (
                <AccountBillingPanel
                  usage={billingUsage.data}
                  organizationId={organizationId}
                />
              )}
            </UserButton.UserProfilePage>
          ) : null}
          {showBilling ? (
            <UserButton.MenuItems>
              <UserButton.Action
                label="Billing"
                labelIcon={<BillingIcon />}
                open={accountBillingClerkPageUrl}
              />
            </UserButton.MenuItems>
          ) : null}
        </UserButton>
      </div>
      <button
        className="rail-account__name"
        type="button"
        tabIndex={expanded ? 0 : -1}
        aria-hidden={!expanded}
        aria-label={`Account menu for ${name}`}
        onClick={openAccountMenu}
      >
        {name}
      </button>
    </div>
  );
}
